import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { parishesApi } from '@/api/endpoints/parishes'

interface Props {
  open: boolean
  onClose: () => void
  onCreated: (parishId: string) => void
}

// USPS 2-letter state codes
const USPS_STATE_CODES = new Set([
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
  'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
  'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
  'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
  'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY',
  'DC', 'AS', 'GU', 'MP', 'PR', 'VI'
])

// Validate phone number - accepts various formats
const isValidPhoneNumber = (phone: string): boolean => {
  if (!phone.trim()) return true // Empty is valid (optional field)
  
  // Remove all non-digit characters
  const digitsOnly = phone.replace(/\D/g, '')
  
  // Must have 10 digits (US phone number)
  return digitsOnly.length === 10
}

export default function ParishSetupModal({ open, onClose, onCreated }: Props) {
  const [name, setName] = useState('')
  const [address, setAddress] = useState('')
  const [city, setCity] = useState('')
  const [stateCode, setStateCode] = useState('')
  const [zip, setZip] = useState('')
  const [officeSame, setOfficeSame] = useState(true)
  const [officeAddress, setOfficeAddress] = useState('')
  const [phones, setPhones] = useState<string[]>([''])
  const [emails, setEmails] = useState<string[]>([''])
  const [website, setWebsite] = useState('')
  const [diocese, setDiocese] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})

  const validateFields = (): boolean => {
    const errors: Record<string, string> = {}
    
    if (!name.trim()) {
      errors.name = 'Parish name is required'
    }
    
    if (!address.trim()) {
      errors.address = 'Parish address is required'
    }
    
    if (!city.trim()) {
      errors.city = 'City is required'
    }
    
    // Validate state code - must be 2-letter USPS code
    const stateUpper = stateCode.trim().toUpperCase()
    if (!stateUpper) {
      errors.stateCode = 'State is required'
    } else if (stateUpper.length !== 2) {
      errors.stateCode = 'State must be a 2-letter code (e.g., NE, CA, NY)'
    } else if (!USPS_STATE_CODES.has(stateUpper)) {
      errors.stateCode = 'Invalid state code. Please use a valid 2-letter USPS state code (e.g., NE, CA, NY)'
    }
    
    if (!zip.trim()) {
      errors.zip = 'ZIP code is required'
    }
    
    // Validate office address if different from parish address
    if (!officeSame && !officeAddress.trim()) {
      errors.officeAddress = 'Office address is required'
    }
    
    // Validate phone numbers if provided
    phones.forEach((phone, index) => {
      if (phone.trim() && !isValidPhoneNumber(phone.trim())) {
        errors[`phone_${index}`] = 'Invalid phone number format. Please use 10 digits (e.g., 4025745867, 402-574-3949, 283.483.4839, or 374 584 4958)'
      }
    })
    
    // Validate email format if provided
    emails.forEach((email, index) => {
      if (email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
        errors[`email_${index}`] = 'Invalid email format'
      }
    })
    
    // Validate website format if provided
    if (website.trim() && !/^https?:\/\/.+/.test(website.trim()) && !/^[a-zA-Z0-9][a-zA-Z0-9-]{1,61}[a-zA-Z0-9]?\.[a-zA-Z]{2,}$/.test(website.trim())) {
      errors.website = 'Please enter a valid website URL (e.g., example.com or https://example.com)'
    }
    
    setFieldErrors(errors)
    return Object.keys(errors).length === 0
  }

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setFieldErrors({})
    
    if (!validateFields()) {
      const errorFields = Object.keys(fieldErrors)
      const errorMessages = errorFields.map(field => {
        let fieldName = field
        // Convert field keys to readable names
        const fieldNameMap: Record<string, string> = {
          name: 'Parish name',
          address: 'Parish address',
          city: 'City',
          stateCode: 'State',
          zip: 'ZIP code',
          officeAddress: 'Office address',
          website: 'Website',
        }
        
        if (fieldNameMap[field]) {
          fieldName = fieldNameMap[field]
        } else if (field.startsWith('email_')) {
          const index = parseInt(field.split('_')[1]) + 1
          fieldName = `Email ${index}`
        } else if (field.startsWith('phone_')) {
          const index = parseInt(field.split('_')[1]) + 1
          fieldName = `Phone ${index}`
        } else {
          // Fallback: convert camelCase to Title Case
          fieldName = field
            .replace(/([A-Z])/g, ' $1')
            .replace(/^./, str => str.toUpperCase())
            .replace(/_/g, ' ')
        }
        return `• ${fieldName}: ${fieldErrors[field]}`
      })
      setError(`Please fix the following errors:\n${errorMessages.join('\n')}`)
      return
    }
    
    try {
      setLoading(true)
      const created = await parishesApi.create({
        name,
        address,
        city,
        state: stateCode,
        zip_code: zip,
        office_address_same: officeSame,
        office_address: officeSame ? address : officeAddress,
        phone_numbers: phones.filter(Boolean),
        email_addresses: emails.filter(Boolean),
        website,
        diocese,
      })
      onCreated(created.id)
      onClose()
    } catch (err: any) {
      const errorMessage = err?.response?.data?.detail || err?.message || 'Failed to create parish'
      setError(errorMessage)
    } finally {
      setLoading(false)
    }
  }

  return (
    <AnimatePresence>
      {open && (
        <motion.div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
      <motion.div
        className="bg-white rounded-xl shadow-xl w-full max-w-2xl p-6 hover-raise"
        initial={{ scale: 0.96, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.96, opacity: 0 }}
        transition={{ type: 'spring', stiffness: 260, damping: 22 }}
      >
        <h2 className="text-xl font-semibold mb-2">First-Time Parish Setup</h2>
        <p className="text-sm text-gray-600 mb-4">Complete these details to get started.</p>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">Parish Name</label>
            <input 
              className={`w-full input-field border-gray-300 focus:outline-none focus:ring-2 focus:ring-yellow-400/60 transition-shadow ${fieldErrors.name ? 'border-red-500 focus:border-red-500 focus:ring-red-200' : ''}`}
              value={name} 
              onChange={(e) => {
                setName(e.target.value)
                if (fieldErrors.name) {
                  setFieldErrors(prev => {
                    const next = { ...prev }
                    delete next.name
                    return next
                  })
                }
              }} 
              required 
            />
            {fieldErrors.name && <p className="mt-1 text-sm text-red-600">{fieldErrors.name}</p>}
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Parish Address</label>
            <input 
              className={`w-full input-field mb-2 focus:outline-none focus:ring-2 focus:ring-yellow-400/60 transition-shadow ${fieldErrors.address ? 'border-red-500 focus:border-red-500 focus:ring-red-200' : ''}`}
              placeholder="Street address" 
              value={address} 
              onChange={(e) => {
                setAddress(e.target.value)
                if (fieldErrors.address) {
                  setFieldErrors(prev => {
                    const next = { ...prev }
                    delete next.address
                    return next
                  })
                }
              }} 
            />
            {fieldErrors.address && <p className="mb-2 text-sm text-red-600">{fieldErrors.address}</p>}
            <div className="grid grid-cols-1 sm:grid-cols-6 gap-3">
              <div className="sm:col-span-3">
                <label className="block text-sm font-medium mb-1">City</label>
                <input 
                  className={`w-full input-field focus:outline-none focus:ring-2 focus:ring-yellow-400/60 transition-shadow ${fieldErrors.city ? 'border-red-500 focus:border-red-500 focus:ring-red-200' : ''}`}
                  value={city} 
                  onChange={(e) => {
                    setCity(e.target.value)
                    if (fieldErrors.city) {
                      setFieldErrors(prev => {
                        const next = { ...prev }
                        delete next.city
                        return next
                      })
                    }
                  }} 
                />
                {fieldErrors.city && <p className="mt-1 text-sm text-red-600">{fieldErrors.city}</p>}
              </div>
              <div className="sm:col-span-2">
                <label className="block text-sm font-medium mb-1">State (2-letter code)</label>
                <input 
                  className={`w-full input-field focus:outline-none focus:ring-2 focus:ring-yellow-400/60 transition-shadow uppercase ${fieldErrors.stateCode ? 'border-red-500 focus:border-red-500 focus:ring-red-200' : ''}`}
                  value={stateCode} 
                  onChange={(e) => {
                    // Auto-uppercase and limit to 2 characters
                    const value = e.target.value.toUpperCase().slice(0, 2)
                    setStateCode(value)
                    if (fieldErrors.stateCode) {
                      setFieldErrors(prev => {
                        const next = { ...prev }
                        delete next.stateCode
                        return next
                      })
                    }
                  }} 
                  placeholder="NE"
                  maxLength={2}
                />
                {fieldErrors.stateCode && <p className="mt-1 text-sm text-red-600">{fieldErrors.stateCode}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">ZIP</label>
                <input 
                  className={`w-full input-field focus:outline-none focus:ring-2 focus:ring-yellow-400/60 transition-shadow ${fieldErrors.zip ? 'border-red-500 focus:border-red-500 focus:ring-red-200' : ''}`}
                  value={zip} 
                  onChange={(e) => {
                    setZip(e.target.value)
                    if (fieldErrors.zip) {
                      setFieldErrors(prev => {
                        const next = { ...prev }
                        delete next.zip
                        return next
                      })
                    }
                  }} 
                />
                {fieldErrors.zip && <p className="mt-1 text-sm text-red-600">{fieldErrors.zip}</p>}
              </div>
            </div>
          </div>
          <div>
            <label className="inline-flex items-center gap-2 text-sm">
              <input type="checkbox" checked={officeSame} onChange={(e) => setOfficeSame(e.target.checked)} />
              <span>Parish office address is the same</span>
            </label>
            {!officeSame && (
              <div className="mt-2">
                <label className="block text-sm font-medium mb-1">Office Address</label>
                <input 
                  className={`w-full input-field focus:outline-none focus:ring-2 focus:ring-yellow-400/60 transition-shadow ${fieldErrors.officeAddress ? 'border-red-500 focus:border-red-500 focus:ring-red-200' : ''}`}
                  value={officeAddress} 
                  onChange={(e) => {
                    setOfficeAddress(e.target.value)
                    if (fieldErrors.officeAddress) {
                      setFieldErrors(prev => {
                        const next = { ...prev }
                        delete next.officeAddress
                        return next
                      })
                    }
                  }} 
                />
                {fieldErrors.officeAddress && <p className="mt-1 text-sm text-red-600">{fieldErrors.officeAddress}</p>}
              </div>
            )}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Parish Phone(s)</label>
              {phones.map((p, i) => (
                <div key={i} className="mb-2">
                  <div className="flex gap-2">
                    <input 
                      className={`flex-1 input-field focus:outline-none focus:ring-2 focus:ring-yellow-400/60 transition-shadow ${fieldErrors[`phone_${i}`] ? 'border-red-500 focus:border-red-500 focus:ring-red-200' : ''}`}
                      value={p} 
                      onChange={(e) => {
                    const next = [...phones]; next[i] = e.target.value; setPhones(next)
                        if (fieldErrors[`phone_${i}`]) {
                          setFieldErrors(prev => {
                            const next = { ...prev }
                            delete next[`phone_${i}`]
                            return next
                          })
                        }
                      }}
                      placeholder="4025745867 or 402-574-3949"
                    />
                    <button 
                      type="button" 
                      className="btn-secondary transition-transform duration-200 hover:scale-[1.02] active:scale-[0.98]" 
                      onClick={() => {
                        setPhones((arr) => arr.filter((_, idx) => idx !== i))
                        setFieldErrors(prev => {
                          const next = { ...prev }
                          delete next[`phone_${i}`]
                          return next
                        })
                      }} 
                      disabled={phones.length === 1}
                    >
                      -
                    </button>
                    <button 
                      type="button" 
                      className="btn-secondary transition-transform duration-200 hover:scale-[1.02] active:scale-[0.98]" 
                      onClick={() => setPhones((arr) => [...arr, ''])}
                    >
                      +
                    </button>
                  </div>
                  {fieldErrors[`phone_${i}`] && <p className="mt-1 text-sm text-red-600">{fieldErrors[`phone_${i}`]}</p>}
                </div>
              ))}
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Parish Email(s)</label>
              {emails.map((em, i) => (
                <div key={i} className="mb-2">
                  <div className="flex gap-2">
                    <input 
                      className={`flex-1 input-field focus:outline-none focus:ring-2 focus:ring-yellow-400/60 transition-shadow ${fieldErrors[`email_${i}`] ? 'border-red-500 focus:border-red-500 focus:ring-red-200' : ''}`}
                      value={em} 
                      onChange={(e) => {
                    const next = [...emails]; next[i] = e.target.value; setEmails(next)
                        if (fieldErrors[`email_${i}`]) {
                          setFieldErrors(prev => {
                            const next = { ...prev }
                            delete next[`email_${i}`]
                            return next
                          })
                        }
                      }} 
                    />
                    <button type="button" className="btn-secondary transition-transform duration-200 hover:scale-[1.02] active:scale-[0.98]" onClick={() => {
                      setEmails((arr) => arr.filter((_, idx) => idx !== i))
                      setFieldErrors(prev => {
                        const next = { ...prev }
                        delete next[`email_${i}`]
                        return next
                      })
                    }} disabled={emails.length === 1}>-</button>
                  <button type="button" className="btn-secondary transition-transform duration-200 hover:scale-[1.02] active:scale-[0.98]" onClick={() => setEmails((arr) => [...arr, ''])}>+</button>
                  </div>
                  {fieldErrors[`email_${i}`] && <p className="mt-1 text-sm text-red-600">{fieldErrors[`email_${i}`]}</p>}
                </div>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Website</label>
              <input 
                className={`w-full input-field focus:outline-none focus:ring-2 focus:ring-yellow-400/60 transition-shadow ${fieldErrors.website ? 'border-red-500 focus:border-red-500 focus:ring-red-200' : ''}`}
                value={website} 
                onChange={(e) => {
                  setWebsite(e.target.value)
                  if (fieldErrors.website) {
                    setFieldErrors(prev => {
                      const next = { ...prev }
                      delete next.website
                      return next
                    })
                  }
                }} 
              />
              {fieldErrors.website && <p className="mt-1 text-sm text-red-600">{fieldErrors.website}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Diocese</label>
              <input 
                className="w-full input-field focus:outline-none focus:ring-2 focus:ring-yellow-400/60 transition-shadow"
                value={diocese} 
                onChange={(e) => setDiocese(e.target.value)} 
              />
            </div>
          </div>
          {error && <div className="text-sm text-red-600 whitespace-pre-line">{error}</div>}
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="btn-secondary transition-transform duration-200 hover:scale-[1.02] active:scale-[0.98]" disabled={loading}>Cancel</button>
            <button type="submit" className="btn-primary transition-transform duration-200 hover:scale-[1.02] active:scale-[0.98] focus:outline-none focus:ring-2 focus:ring-yellow-400/60" disabled={loading}>{loading ? 'Saving…' : 'Save'}</button>
          </div>
        </form>
      </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}


